import { useEffect, useRef } from 'react';
import { Icon } from '../../components/ui/Icon.jsx';
import styles from './Lightbox.module.css';

/**
 * Full-screen image viewer.
 *
 * Built on the native <dialog>: showModal() gives focus containment,
 * Escape-to-close, an inert background and top-layer stacking for free.
 * The previous implementation hand-rolled all four, and the focus trap
 * alone was forty lines of keydown bookkeeping.
 *
 * @param {object[]} items  [{ src, alt, caption }]
 * @param {number|null} index  the open item, or null when closed
 */
export function Lightbox({ items, index, onClose, onIndexChange }) {
  const dialogRef = useRef(null);
  const open = index !== null && index >= 0 && index < items.length;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Escape and the backdrop's own dismiss both fire "cancel"/"close" on the
  // element rather than going through our button, so the parent's state has
  // to be told either way or it would think the dialog is still open.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  const multiple = items.length > 1;

  const step = (delta) => {
    onIndexChange((index + delta + items.length) % items.length);
  };

  useEffect(() => {
    if (!open || !multiple) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  const item = open ? items[index] : null;

  return (
    <dialog
      ref={dialogRef}
      className={styles.lightbox}
      aria-label="Project image viewer"
      // Clicking the backdrop closes; clicking the figure or a control does
      // not. The dialog element itself *is* the backdrop area here, since
      // the padding around the figure belongs to it.
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <button type="button" className={`${styles.btn} ${styles.close}`} aria-label="Close image viewer" onClick={onClose}>
        <Icon name="close" strokeWidth={1.75} />
      </button>

      {multiple ? (
        <>
          <button type="button" className={`${styles.btn} ${styles.prev}`} aria-label="Previous image" onClick={() => step(-1)}>
            <Icon name="chevronLeft" strokeWidth={1.75} />
          </button>
          <button type="button" className={`${styles.btn} ${styles.next}`} aria-label="Next image" onClick={() => step(1)}>
            <Icon name="chevronRight" strokeWidth={1.75} />
          </button>
        </>
      ) : null}

      {item ? (
        <figure className={styles.figure}>
          {/* Rendered only while open, so the browser never fetches a
              full-size image for a viewer nobody has asked for. */}
          <img className={styles.img} src={item.src} alt={item.alt ?? ''} />
          <figcaption>
            <p className={styles.caption}>{item.caption}</p>
            {multiple ? (
              <p className={styles.count}>{index + 1} of {items.length}</p>
            ) : null}
          </figcaption>
        </figure>
      ) : null}
    </dialog>
  );
}
